import { NumberRank, numberRankValues } from "src/app/enums/number/number-rank.enum";
import { StyleNumber } from "src/app/enums/number/StyleNumber.enum";
import { Unit } from "src/app/models/unit";
import { environment } from "src/environments/environment";
import { GlobalUtil } from "./global.util";

export type MathRounderFunction = 'round' | 'ceil' | 'floor';

export class NumberUtil {

  static REGEX_INTEGER = '^-?[0-9]+$';
  static REGEX_FLOAT = '^-?[0-9]+(\.|,)?[0-9]+$';

  // ----------------------------------------------------------------------- //
  //                              A TESTER                                   //
  // ----------------------------------------------------------------------- //
  public static isInteger(value: string){
    return GlobalUtil.checkRegex(value, NumberUtil.REGEX_INTEGER);
  }

  public static isIntegerOrNull(value: string){
    return value === null || NumberUtil.isInteger(value);
  }

  public static isFloat(value: string){
    return GlobalUtil.checkRegex(value, NumberUtil.REGEX_FLOAT);
  }

  // -------------------------------------------
  //                VALIDE nlas
  // -------------------------------------------
  /**
   * si 1  => '01'
   * si 10 => '10'
   */
   public static getZeroFormat(value: number): string {
    return value < 10 ? '0'+value : value+'';
  }

  public static isNumber(value: string | number): boolean {
    if (!isNaN(Number(value))) {
      return true;
    } else {
      return false;
    }
  }

  public static isNegative(value: any): boolean {
    return NumberUtil.isNumber(value) && value < 0;
  }

  public static isNegativeInteger(value: any): boolean {
    try {
      return Number.isInteger(Number(value)) && value < 0;
    } catch (error) {
      return false;
    }
  }

  public static isNegativeOrZero(value: any) {
    return NumberUtil.isNegative(value) || value == 0;
  }

  public static isNegativeOrZeroOrNull(value: any) {
    return NumberUtil.isNegative(value) || value == 0 || value === null;
  }

  public static IsNegativeOrZeroIntegerOrNull(value: any) {
    return NumberUtil.isNegativeInteger(value) || value == 0 || value === null;
  }

  public static toNumber(val: string | null | undefined): number {
    if (val !== null && val !== undefined && NumberUtil.isNumber(val)) {
      return parseFloat(val);
    }
    return 0;
  }

  public static toNumberOrNull(val: string | null | undefined): number | null {
    if (val !== null && val !== undefined && NumberUtil.isNumber(val)) {
      return parseFloat(val);
    }
    return null;
  }

  /**
   * retourne un nombre destiné à l'affichage : string avec un format précis
   * @param value
   * @param lang BCP47  => format : xx-XX (pays-region)
   *             pays   => https://fr.wiktionary.org/wiki/Wiktionnaire:BCP_47/language-2
   *             region => https://fr.wiktionary.org/wiki/Wiktionnaire:BCP_47/region-2
   * @param style : 'decimal' | 'curency' | 'percent' : option "style" => https://developer.mozilla.org/fr/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat
   * @param currency : ISO 4217 : https://fr.wikipedia.org/wiki/ISO_4217
   */
   static getNumber(value: number, lang: string, style: StyleNumber = StyleNumber.DECIMAL, fixed: number = 2, currency: string | undefined = undefined, rank: NumberRank = NumberRank.UNIT): string {
    try {
      style = style === StyleNumber.CURRENCY || style === StyleNumber.DURATION  ? StyleNumber.DECIMAL : style; // @@@ quick fix. en attente car non automatiser pour maximum perfs (cause pipe pure false)
      // currency = style !== StyleNumber.CURRENCY ? currency : 'EUR' ; // @@@ quick fix en attente
      return new Intl.NumberFormat(lang, {
        style,
        currency,
        minimumFractionDigits: fixed,
        maximumFractionDigits: fixed
      }).format(style === StyleNumber.PERCENT ? value : value/rank) + NumberUtil.getRankUnit(rank, style);
    } catch (error) {
      return '';
    }
  }

  // donne le nombre de decimales en fonction de son type
  static getFixedByStyle(style: StyleNumber): number {
    switch (style) {
      case StyleNumber.CURRENCY:
        return environment.core.data.fixedCurrency;
      case StyleNumber.DECIMAL:
        return environment.core.data.fixedDecimal;
      case StyleNumber.PERCENT:
        return environment.core.data.fixedPercent;
      case StyleNumber.DURATION:
        return environment.core.data.fixedDuration
    }
  }

  /**
   * Retourne l'unité d'affichage d'un nombre
   * on pourrait ajouter au param "StyleNumber" poids(mg,g,kg,T) etc....
   * Percent retourne '' car le js l'affiche naivement
   */
  static getRankUnit(rank: NumberRank, style: StyleNumber): string {
    if (style === StyleNumber.CURRENCY || style === StyleNumber.DECIMAL) {
      switch (rank) {
        case NumberRank.UNIT:
          return '';
        case NumberRank.THOUSAND:
          return ' k';
        case NumberRank.MILLIONS:
          return ' M';
        case NumberRank.BILLION:
          return ' G';
      }
    }
    else if (style === StyleNumber.PERCENT) {
      return '';
    } else if (style === StyleNumber.DURATION) {
      return '';
    }
    return '';
  }

  // retourne le style de nombre en fonction du type donné par api
  static unitApiMapper(unit: Unit | undefined): StyleNumber {
    switch (unit) {
      case Unit.CURRENCY:
        return StyleNumber.CURRENCY;
      case Unit.NUMBER:
        return StyleNumber.DECIMAL;
      case Unit.PERCENT:
        return StyleNumber.PERCENT;
      case Unit.DURATION:
        return StyleNumber.DURATION;
      default:
        return environment.core.data.defaultStyle;
    }
  }

  /**
   * Arrondir au significatif
   * @param value la valeur à arrondir
   * @param significant le nombre de chiffre significatif voulu
   * @param method la méthode d'arrondi : Math.floor, Math.ceil ou Math.round
   * @returns la valeur arrondie au nombre de chiffre significatif selon la méthode définie
   */
  static roundToSignificantFigure(value: number, significant: number, method: MathRounderFunction): number{
    const digit = Math.floor(Math.log10(Math.abs(value))) - (significant - 1);
    const rounder = digit < 0 ? 0 : digit;
    let math;
    switch (method) {
      case 'round':
        math = Math.round;
        break;
      case 'floor':
        math = Math.floor;
        break;
      case 'ceil':
        math = Math.ceil;
        break;
      default:
        math = Math.round;
    }
    try {
      return math(value / Math.pow(10, rounder)) * Math.pow(10, rounder);
    } catch(error) {
      console.error(error);
      return value;
    }
  }

  static getHigherRank(value: number, rank: NumberRank): NumberRank{
    const i = numberRankValues().indexOf(rank);
    if(value / rank < 1 || i === numberRankValues().length - 1) return rank;
    if (value / numberRankValues()[i+1] < 1) return rank;
    if (i + 1 === numberRankValues().length - 1) return numberRankValues()[i+1];
    return NumberUtil.getHigherRank(value, numberRankValues()[i+1]);
  }
  static getFormatNumber(value: number, precision: number): number {
    return parseFloat(value.toFixed(precision))
  }
}
